import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { SiteSearchService, SiteSearchDataModel } from '../../services/site-search.service';
import { Observable, EMPTY } from 'rxjs';
import { debounceTime, switchMap, catchError, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SiteSearchDialogComponent } from '../site-search-dialog/site-search-dialog.component';
import { Router } from '@angular/router';
import { LoanDetailsListOfLoansService } from '../../loan-details-modal/loan-details-list-of-loans.service';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { UntypedFormControl } from '@angular/forms';
import { State } from '@progress/kendo-data-query';
import { ProfitabilityService } from 'app/modules/incentives/services/profitability.service';

@Component({
    selector: 'site-search',
    templateUrl: './site-search.component.html',
    styleUrls: ['./site-search.component.scss'],
})
export class SiteSearchComponent implements OnInit {
    @ViewChild('siteSearchInput') searchElement: ElementRef;
    @Input() insightSearch?: boolean = false;
    results$: Observable<SiteSearchDataModel[]>;
    searchActive: boolean = false;
    data: SiteSearchDataModel[];
    loading = false;
    searchInput: any;
    employees: SiteSearchDataModel[];
    entities: SiteSearchDataModel[];
    loans: SiteSearchDataModel[];
    noDataType: NoDataTypes;
    error: any = undefined;
    noResults: boolean = false;
    inputTerm = new UntypedFormControl('');
    allResults: boolean = false;
    isSiteSearchDialogOpen: boolean;
    searchTypes: string[] = ['L', 'E', 'H'];
    state: State = {
        skip: 0,
        take: 5,
        group: [
            {
                field: 'rowType',
                dir: 'desc',
            },
        ],
        sort: [
            {
                field: 'name',
                dir: 'asc',
            },
        ],
    };
    constructor(
        private siteSearchService: SiteSearchService,
        public dialog: MatDialog,
        private listOfLoansService: LoanDetailsListOfLoansService,
        private router: Router,
        private profitabilityService: ProfitabilityService
    ) {}

    openModal(SelectedLoanNumber: string) {
        if (this.insightSearch) {
            const profitabilityParams = {
                fromDatePeriod: 'This Year',
                loanNumber: SelectedLoanNumber,
            };
            this.profitabilityService.setLoanPrfitabilityDialogParams(profitabilityParams, true);
            this.profitabilityService.openLoanProfitabilityDialog();
        } else {
            let loanNumbers: string[] = this.data
                .filter((x) => x.rowType === 'L')
                .map((x) => x.identifier);
            let index = loanNumbers.findIndex((loanNumber) => loanNumber === SelectedLoanNumber);
            this.listOfLoansService.changeListOfLoans({
                loanNumbers: loanNumbers,
                index: index,
            });
            this.router.navigate([
                { outlets: { modal: 'loanDetails' + '/' + SelectedLoanNumber } },
            ]);
            this.closeInput();
        }
    }

    ngOnInit(): void {
        this.loans = [];
        this.employees = [];
        this.entities = [];
        this.isSiteSearchDialogOpen = false;
        this.noDataType = {
            action: NoDataActionType.search,
            searchTerm: '',
            componentTitle: 'Search Results',
            inputSource: NoDataSourceType.table,
        };
        if (this.insightSearch) this.searchTypes = ['L'];
        this.watchForm();
    }

    watchForm() {
        this.inputTerm.valueChanges
            .pipe(
                tap((term) => {
                    this.isSiteSearchDialogOpen = false;
                    this.clearTypes();
                    this.data = [];
                    this.noResults = false;
                    if (term === '') {
                        return this.closeMenu();
                    }
                    this.loading = true;
                }),
                debounceTime(500),
                switchMap((term: string) => {
                    return this.siteSearchService
                        .getSiteSearchResults(
                            term,
                            this.searchTypes.toString(),
                            this.state.skip,
                            this.state.take,
                            '2A',
                            this.allResults
                        )
                        .pipe(
                            catchError((error) => {
                                return this.handleError(error);
                            })
                        );
                })
            )
            .subscribe(
                (data) => {
                    //if no loans are found then search all loans
                    if (!(data.findIndex((obj) => obj.rowType === 'L') + 1) && !this.allResults) {
                        this.allResults = true;
                        this.inputTerm.setValue(this.inputTerm.value);
                        return;
                    }
                    this.allResults = false;
                    this.loading = false;
                    if (this.inputTerm.value !== '') {
                        this.data = data;
                        if (data && data.length) {
                            this.getTypes();
                        } else {
                            this.noResults = true;
                            this.noDataType.action = NoDataActionType.search;
                            this.noDataType.searchTerm = this.inputTerm.value;
                        }
                        this.openMenu();
                    }
                },
                (error) => {
                    this.handleError(error);
                }
            );
    }

    handleError(error): Observable<any> {
        this.error = error;
        this.noResults = true;
        this.noDataType.action = NoDataActionType.error;
        this.loading = false;
        this.openMenu();
        return EMPTY;
    }

    getTypes() {
        this.loans = this.data.filter((item) => item.rowType === 'L');
        this.employees = this.data.filter((item) => item.rowType === 'E' && !this.insightSearch);
        this.entities = this.data.filter((item) => item.rowType === 'H' && !this.insightSearch);
    }

    clearTypes() {
        this.loans = [];
        this.employees = [];
        this.entities = [];
    }

    openInput() {
        this.searchActive = true;
        this.searchElement.nativeElement.style.display = 'inline-flex';
        this.searchElement.nativeElement.focus();
    }

    closeInput() {
        this.searchActive = false;
        this.searchElement.nativeElement.style.display = 'none';
        this.inputTerm.setValue('');
        this.closeMenu();
    }

    openDialog(dataType: string, id: string = ''): void {
        if (this.data?.length) {
            this.dialog.open(SiteSearchDialogComponent, {
                data: {
                    results: this.data,
                    search: this.inputTerm.value,
                    dataType: dataType,
                    employeeIdentifier: id,
                },
            });
            this.closeInput();
        }
    }

    openEmployee(id: string = '') {
        this.router.navigate([`/settings/employees/${id}`]);
        this.closeInput();
    }

    openEntity(id: string = '') {
        this.router.navigate([`/directory/record/${id}`]);
        this.closeInput();
    }

    openMenu() {
        if (
            ((this.inputTerm.value !== '' && this.noResults === true) ||
                this.data?.length > 0 ||
                (this.error != undefined && this.searchActive)) &&
            this.loading === false
        ) {
            this.isSiteSearchDialogOpen = true;
        }
    }

    closeMenu() {
        this.isSiteSearchDialogOpen = false;
        this.loading = false;
    }
}
